const roundMoney = (value = 0) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export { getDdtStatusColor, getDdtStatusLabel, isDdtChiusoPerCicloPassivo } from './ddtStatus';

const toNumber = (value, fallback = 0) => {
    const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const unique = (items = []) => [...new Set(items.filter(Boolean))];

const normalizeKey = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

const unwrapRelation = (value) => {
    if (!value) return null;
    if (value.data) return unwrapRelation(value.data);
    if (value.attributes) {
        return {
            ...value.attributes,
            id: value.id ?? value.attributes.id,
            documentId: value.documentId ?? value.attributes.documentId,
        };
    }
    return value;
};

const formatLegacyDate = (dateValue) => {
    if (!dateValue || typeof dateValue !== 'string') return 'n/d';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) return dateValue;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-');
        return `${day}/${month}/${year}`;
    }
    return dateValue;
};

export const formatMoney = (value = 0) => `${roundMoney(value).toFixed(2)} €`;

export const getReturnDdtStatusLabel = (status) => {
    const labels = {
        bozza: 'Bozza',
        inviato: 'Inviato al fornitore',
        chiuso: 'Chiuso',
    };

    return labels[status] || status;
};

export const getReturnDdtStatusColor = (status) => {
    const colors = {
        bozza: 'default',
        inviato: 'processing',
        chiuso: 'success',
    };

    return colors[status] || 'default';
};

const buildGroupMap = (groups = []) => {
    return groups.reduce((accumulator, group) => {
        [
            group.id,
            group.documentId,
            group.strapiId,
            group.numericId,
            group.label,
            group.name,
            group.nome,
        ]
            .flatMap((key) => [key, normalizeKey(key)])
            .filter(Boolean)
            .forEach((key) => {
                accumulator[key] = group;
            });
        return accumulator;
    }, {});
};

const getGroupPurchaseAccount = (group) => {
    const account = unwrapRelation(group?.conto_dare);
    return (
        account?.documentId ||
        account?.codice ||
        account?.code ||
        account?.descrizione ||
        account?.description ||
        account?.name ||
        group?.purchaseAccount ||
        null
    );
};

const buildArticleMap = (articles = []) => {
    return articles.reduce((accumulator, article) => {
        [
            article.id,
            article.documentId,
            article.id_maffle,
            article.code,
            article.label,
            article.name,
            ...(article.aliases || []),
        ]
            .map(normalizeKey)
            .filter(Boolean)
            .forEach((key) => {
                accumulator[key] = article;
            });
        return accumulator;
    }, {});
};

const isSerialLikeValue = (value, line = {}) => {
    const normalized = normalizeKey(value);
    if (!normalized) return false;
    return [
        line.serialNumber,
        line.lotCode,
        line.id,
        line.lotId,
        line.lotDocumentId,
    ]
        .map(normalizeKey)
        .filter(Boolean)
        .includes(normalized);
};

const getDdtProductIdentity = (line = {}) => {
    const candidates = [
        line.productIdentity,
        line.productId,
        line.productDocumentId,
        line.productCode,
        line.productName,
        line.description,
        line.articleCode,
        line.groupId,
        line.groupLabel,
    ];

    return (
        candidates
            .map((candidate) => String(candidate || '').trim())
            .find((candidate) => candidate && !isSerialLikeValue(candidate, line)) ||
        'articolo-sconosciuto'
    );
};

const getAcubePayload = (invoice) => invoice?.acubeEvent?.invoice?.payload || null;

const getAcubeBody = (invoice) => {
    const payload = getAcubePayload(invoice);
    const body = payload?.fattura_elettronica_body;
    return Array.isArray(body) ? body[0] : body || null;
};

const getAcubeDocument = (invoice) => {
    return getAcubeBody(invoice)?.dati_generali?.dati_generali_documento || null;
};

const getAcubeSupplier = (invoice) => {
    const payload = getAcubePayload(invoice);
    return payload?.fattura_elettronica_header?.cedente_prestatore?.dati_anagrafici || null;
};

const getAcubeRiepilogo = (invoice) => {
    const rows = getAcubeBody(invoice)?.dati_beni_servizi?.dati_riepilogo;
    return Array.isArray(rows) ? rows : rows ? [rows] : [];
};

const getAcubeLineItems = (invoice) => {
    const rows = getAcubeBody(invoice)?.dati_beni_servizi?.dettaglio_linee;
    return Array.isArray(rows) ? rows : rows ? [rows] : [];
};

/**
 * Estrae da una fattura Billing Hub i dati di testata usati dalla stampa PDF.
 * Esportata perche' serve anche fuori dal workspace passivo (prima nota), dove
 * si parte dalla sola fattura remota senza lo stato di lavorazione.
 */
export const extractInvoiceEssentials = (invoice) => {
    const supplier = getAcubeSupplier(invoice);
    const document = getAcubeDocument(invoice);
    const riepilogo = getAcubeRiepilogo(invoice);
    const supplierName =
        invoice.xmlSupplierName ||
        supplier?.anagrafica?.denominazione ||
        supplier?.anagrafica?.nome ||
        'Fornitore';
    const supplierVat =
        invoice.xmlSupplierVat ||
        supplier?.id_fiscale_iva?.id_codice ||
        '';
    const supplierReferenceNumber = invoice.supplierReferenceNumber || document?.numero || '';
    const issueDate = invoice.issueDate || document?.data || null;
    const isCreditNote = invoice.documentType === 'TD04' || document?.tipo_documento === 'TD04';
    const net = roundMoney(
        riepilogo.reduce((total, item) => total + toNumber(item?.imponibile_importo, 0), 0)
    ) * (isCreditNote ? (riepilogo.some((item) => toNumber(item?.imponibile_importo, 0) < 0) ? -1 : 1) : 1);
    const vat = roundMoney(
        riepilogo.reduce((total, item) => total + toNumber(item?.imposta, 0), 0)
    ) * (isCreditNote ? (riepilogo.some((item) => toNumber(item?.imposta, 0) < 0) ? -1 : 1) : 1);
    // Il totale dichiarato dal fornitore, non la somma dei riepiloghi: e' quello
    // che il backend usa per il debito verso il fornitore. Mostrarlo com'e'
    // rende visibile un documento incoerente invece di mascherarlo.
    const declaredGross = document?.importo_totale_documento !== undefined
        ? roundMoney(isCreditNote ? Math.abs(toNumber(document.importo_totale_documento, 0)) : toNumber(document.importo_totale_documento, 0))
        : null;

    // I totali gia' normalizzati hanno la precedenza, ma il totale dichiarato va
    // aggiunto anche quando arrivano da una normalizzazione precedente che non
    // lo conosceva: altrimenti un `||` lo scarterebbe in silenzio.
    const existingTotals = invoice.xmlTotals || { net, vat };

    return {
        xmlSupplierName: supplierName,
        xmlSupplierVat: supplierVat,
        supplierReferenceNumber,
        issueDate,
        xmlTotals: {
            ...existingTotals,
            declaredGross: existingTotals.declaredGross ?? declaredGross,
        },
    };
};

const buildAcubeSummary = (invoice, normalized) => {
    const acubeInvoice = invoice?.acubeEvent?.invoice;
    const document = getAcubeDocument(invoice);
    const riepilogo = getAcubeRiepilogo(invoice);

    return {
        metadata: [
            { label: 'UUID', value: acubeInvoice?.uuid || 'n/d' },
            { label: 'Filename SDI', value: acubeInvoice?.filename || 'n/d' },
            { label: 'Ricevuta il', value: acubeInvoice?.created_at || 'n/d' },
            { label: 'Stato documento', value: acubeInvoice?.marking || 'received' },
            { label: 'Fornitore', value: `${normalized.xmlSupplierName} · ${normalized.xmlSupplierVat}`.trim() },
            { label: 'Documento', value: `${supplierDocumentLabel(document?.tipo_documento)} ${normalized.supplierReferenceNumber} del ${normalized.issueDate || 'n/d'}`.trim() },
        ],
        totals: {
            net: normalized.xmlTotals?.net || 0,
            vat: normalized.xmlTotals?.vat || 0,
            gross: roundMoney((normalized.xmlTotals?.net || 0) + (normalized.xmlTotals?.vat || 0)),
        },
        riepilogo: riepilogo.map((item, index) => ({
            id: `riepilogo-${index}`,
            vatRate: toNumber(item?.aliquota_iva, 22),
            net: invoice.documentType === 'TD04' ? Math.abs(toNumber(item?.imponibile_importo, 0)) : toNumber(item?.imponibile_importo, 0),
            vat: invoice.documentType === 'TD04' ? Math.abs(toNumber(item?.imposta, 0)) : toNumber(item?.imposta, 0),
        })),
        lineItems: getAcubeLineItems(invoice).map((line, index) => ({
            id: `acube-line-${index}`,
            lineNumber: line?.numero_linea || index + 1,
            description: line?.descrizione || 'Riga fattura',
            quantity: toNumber(line?.quantita, 1) || 1,
            unitPrice: invoice.documentType === 'TD04' ? Math.abs(toNumber(line?.prezzo_unitario, 0)) : toNumber(line?.prezzo_unitario, 0),
            lineTotal: invoice.documentType === 'TD04' ? Math.abs(toNumber(line?.prezzo_totale, 0)) : toNumber(line?.prezzo_totale, 0),
            vatRate: toNumber(line?.aliquota_iva, 22),
        })),
    };
};

const buildLegacyXmlPreview = (normalized) => [
    { label: 'Cedente', value: `${normalized.xmlSupplierName} · ${normalized.xmlSupplierVat}`.trim() },
    { label: 'Documento', value: `${normalized.supplierReferenceNumber} del ${formatLegacyDate(normalized.issueDate)}`.trim() },
    { label: 'Imponibile', value: formatMoney(normalized.xmlTotals?.net || 0) },
    { label: 'IVA', value: formatMoney(normalized.xmlTotals?.vat || 0) },
];

const supplierDocumentLabel = (type) => {
    const labels = {
        TD01: 'Fattura',
        TD04: 'Nota di credito',
        TD05: 'Nota di debito',
    };

    return labels[type] || type || 'Documento';
};

const buildComputedLine = ({
    line,
    sourceType,
    sourceLabel,
    overrides = {},
    groupMap,
    articleMap,
    fallbackAccount,
}) => {
    const overriddenArticle = overrides.productId
        ? articleMap[normalizeKey(overrides.productId)] || null
        : null;
    const article =
        overriddenArticle ||
        articleMap[normalizeKey(line.productId)] ||
        articleMap[normalizeKey(line.articleCode)] ||
        articleMap[normalizeKey(line.description)] ||
        null;
    const articleGroup = unwrapRelation(article?.merchandise_group || article?.merchandiseGroup || article?.group);
    const selectedGroupId = overrides.groupId || articleGroup?.documentId || articleGroup?.id;
    const group =
        groupMap[selectedGroupId] ||
        groupMap[normalizeKey(selectedGroupId)] ||
        groupMap[line.groupId] ||
        groupMap[normalizeKey(line.groupId)] ||
        groupMap[line.groupDocumentId] ||
        groupMap[normalizeKey(line.groupDocumentId)] ||
        groupMap[line.groupStrapiId] ||
        groupMap[normalizeKey(line.groupStrapiId)] ||
        groupMap[article?.groupId] ||
        groupMap[normalizeKey(article?.groupId)] ||
        groupMap[line.groupLabel] ||
        groupMap[normalizeKey(line.groupLabel)] ||
        null;
    const matchedQuantity = toNumber(line.matchedQuantity ?? line.quantity, 0);
    const matchedUnitPrice = toNumber(line.matchedUnitPrice ?? line.unitPrice, 0);
    const matchedDiscount = toNumber(line.matchedDiscount ?? line.discount, 0);
    const quantity = toNumber(overrides.quantity ?? line.quantity, 0);
    const unitPrice = toNumber(overrides.unitPrice ?? line.unitPrice, 0);
    const discount = toNumber(overrides.discount ?? line.discount, 0);
    const vatRate = toNumber(overrides.vatRate ?? line.vatRate ?? group?.vatRate, 22);
    const groupPurchaseAccount = getGroupPurchaseAccount(group) || getGroupPurchaseAccount(articleGroup);
    const defaultPurchaseAccount = groupPurchaseAccount || article?.purchaseAccount || line.purchaseAccount || fallbackAccount || null;
    const purchaseAccount = overrides.purchaseAccount !== undefined
        ? overrides.purchaseAccount
        : defaultPurchaseAccount;
    const ddtVarianceReason = overrides.ddtVarianceReason ?? line.ddtVarianceReason ?? '';
    const productName =
        overrides.productName ||
        article?.name ||
        article?.label ||
        line.productName ||
        line.articleName ||
        null;
    const matchedNet =
        line.matchedNet !== undefined
            ? roundMoney(line.matchedNet)
            : roundMoney(matchedQuantity * matchedUnitPrice * (1 - matchedDiscount));
    const hasDdtPricingOverride = ['quantity', 'unitPrice', 'discount']
        .some((field) => overrides[field] !== undefined);
    const net = sourceType === 'ddt' && !hasDdtPricingOverride && line.matchedNet !== undefined
        ? matchedNet
        : roundMoney(quantity * unitPrice * (1 - discount));
    const vat = roundMoney(net * vatRate / 100);
    const matchedVat =
        line.matchedVat !== undefined
            ? roundMoney(line.matchedVat)
            : roundMoney(matchedNet * vatRate / 100);
    const isManualOrServiceLine = sourceType === 'extra' || sourceType === 'direct';
    const description = isManualOrServiceLine
        ? (
            overrides.description ||
            line.description ||
            productName ||
            article?.label ||
            line.label ||
            'Riga documento'
        )
        : (
            overrides.description ||
            productName ||
            line.description ||
            article?.label ||
            line.label ||
            'Riga documento'
        );

    return {
        id: line.id,
        sourceType,
        sourceLabel,
        sourceLineIds: line.sourceLineIds || [line.id],
        ddtSourceLineIds: sourceType === 'ddt'
            ? unique(line.detailSourceLineIds || line.ddtSourceLineIds || [])
            : [],
        productId: overrides.productId || article?.documentId || line.productId || null,
        productName,
        articleName: productName,
        articleCode: article?.id_maffle || article?.code || line.articleCode || line.id,
        description,
        groupId: selectedGroupId || line.groupId || article?.groupId || group?.id || 'generico',
        groupLabel: overrides.groupLabel || articleGroup?.nome || articleGroup?.name || line.groupLabel || group?.label || 'Generico',
        quantity,
        unitPrice,
        discount,
        vatRate,
        purchaseAccount,
        ddtVarianceReason,
        isService: Boolean(line.isService || sourceType === 'extra'),
        isRepair: Boolean(line.isRepair),
        matchedQuantity,
        matchedUnitPrice,
        matchedNet,
        matchedVat,
        net,
        vat,
        gross: roundMoney(net + vat),
        netDifference: roundMoney(net - matchedNet),
        vatDifference: roundMoney(vat - matchedVat),
        ddtReferences: unique(line.ddtReferences || []),
        detailLines: line.detailLines || [],
    };
};

const aggregateSelectedDdtLines = (selectedDdts = [], invoice = {}) => {
    const grouped = {};

    selectedDdts.forEach((ddt) => {
        const ddtReference =
            unique([ddt.supplierDocumentNumber, ddt.internalNumber].filter(Boolean)).join(' · ') ||
            ddt.id;

        (ddt.lines || []).forEach((line, index) => {
            const quantity = toNumber(line.quantity, 1) || 1;
            const unitPrice = toNumber(line.unitPrice, 0);
            const discount = toNumber(line.discount, 0);
            const vatRate = toNumber(line.vatRate, 22);
            const articleCode = line.articleCode || null;
            const groupId = line.groupId || null;
            const groupDocumentId = line.groupDocumentId || null;
            const groupStrapiId = line.groupStrapiId || null;
            const groupLabel = line.groupLabel || null;
            const productName = line.productName || line.articleName || null;
            const rawDescription = line.description || articleCode || `Riga ${index + 1}`;
            const descriptionLooksSerial = isSerialLikeValue(rawDescription, line);
            const description =
                productName ||
                (descriptionLooksSerial ? groupLabel || 'Articolo non risolto' : rawDescription);
            const productIdentity = getDdtProductIdentity({ ...line, description });
            const purchaseAccount = line.purchaseAccount || null;
            const salesAccount = line.salesAccount || null;
            const key = [
                ddt.id || ddt.documentId || ddtReference,
                normalizeKey(productIdentity),
            ].join('__');

            if (!grouped[key]) {
                grouped[key] = {
                    id: `ddt-line-${key}`,
                    productId: productIdentity,
                    productIdentity,
                    productName,
                    articleName: productName,
                    articleCode,
                    description,
                    groupId,
                    groupDocumentId,
                    groupStrapiId,
                    groupLabel,
                    purchaseAccount,
                    salesAccount,
                    quantity: 0,
                    matchedNet: 0,
                    matchedVat: 0,
                    vatRate,
                    isService: Boolean(line.isService),
                    isRepair: Boolean(line.isRepair),
                    sourceLineIds: [],
                    ddtReferences: [],
                    detailLines: [],
                };
            }

            if (!grouped[key].purchaseAccount && purchaseAccount) {
                grouped[key].purchaseAccount = purchaseAccount;
            }
            if (!grouped[key].salesAccount && salesAccount) {
                grouped[key].salesAccount = salesAccount;
            }
            if (!grouped[key].groupDocumentId && groupDocumentId) {
                grouped[key].groupDocumentId = groupDocumentId;
            }
            if (!grouped[key].groupStrapiId && groupStrapiId) {
                grouped[key].groupStrapiId = groupStrapiId;
            }
            if (!grouped[key].productName && productName) {
                grouped[key].productName = productName;
                grouped[key].articleName = productName;
                grouped[key].description = productName;
            }

            const matchedNet = roundMoney(quantity * unitPrice * (1 - discount));
            const matchedVat = roundMoney(matchedNet * vatRate / 100);
            grouped[key].quantity = roundMoney(grouped[key].quantity + quantity);
            grouped[key].matchedNet = roundMoney(grouped[key].matchedNet + matchedNet);
            grouped[key].matchedVat = roundMoney(grouped[key].matchedVat + matchedVat);
            grouped[key].sourceLineIds.push(line.id || `${ddt.id}-line-${index}`);
            grouped[key].ddtReferences.push(ddtReference);
            grouped[key].detailLines.push({
                id: `${ddt.id}-${line.id || index}`,
                ddtReference,
                productName,
                articleName: productName,
                articleCode,
                description,
                quantity,
                unitPrice,
                discount,
                net: matchedNet,
                vatRate,
                vat: matchedVat,
                productId: line.productId || null,
                groupId,
                groupDocumentId,
                groupStrapiId,
                groupLabel,
                purchaseAccount,
                salesAccount,
                isRepair: Boolean(line.isRepair),
                repairNote: line.repairNote || null,
                repairInWarranty: Boolean(line.repairInWarranty),
                serialNumber: line.serialNumber || null,
                lotCode: line.lotCode || null,
            });
        });
    });

    return Object.values(grouped).map((line) => {
        const override = invoice.lineOverrides?.[line.id] || {};
        const matchedUnitPrice = line.quantity > 0 ? roundMoney(line.matchedNet / line.quantity) : 0;

        return {
            ...line,
            quantity: override.quantity ?? line.quantity,
            unitPrice: override.unitPrice ?? matchedUnitPrice,
            discount: override.discount ?? 0,
            ddtVarianceReason: override.ddtVarianceReason ?? line.ddtVarianceReason ?? '',
            matchedQuantity: line.quantity,
            matchedUnitPrice,
            sourceLineIds: [line.id],
            detailSourceLineIds: line.sourceLineIds,
            sourceLabel: unique(line.ddtReferences).join(', '),
        };
    });
};

const buildJournalDraft = (lines = [], protocol, calculatedVat = 0, vatAccount = null, vatSection = 'Dare', isCreditNote = false) => {
    // Le righe a zero (rettifiche non ancora compilate) non sono movimenti:
    // non entrano nella bozza, ne' da sole ne' come etichetta di un gruppo.
    const grouped = lines.filter((line) => roundMoney(line.net || 0) !== 0 || roundMoney(line.vat || 0) !== 0).reduce((accumulator, line) => {
        const key = line.purchaseAccount;

        if (!accumulator[key]) {
            accumulator[key] = {
                id: key,
                account: line.purchaseAccount,
                amount: 0,
                sourceLineIds: [],
                categories: [],
            };
        }

        accumulator[key].amount = roundMoney(accumulator[key].amount + line.net);
        accumulator[key].sourceLineIds.push(...(line.sourceLineIds || [line.id]));
        accumulator[key].categories.push(line.productName || line.description || line.groupLabel);
        return accumulator;
    }, {});

    const expenseRows = Object.values(grouped)
        .filter((row) => row.amount !== 0)
        .map((row, index) => ({
            id: `journal-expense-${index}`,
            section: isCreditNote ? 'Avere' : 'Dare',
            account: row.account,
            amount: row.amount,
            categories: unique(row.categories),
            sourceLineIds: unique(row.sourceLineIds),
            editable: true,
            note: protocol,
        }));

    const totalVat = roundMoney(calculatedVat);
    if (totalVat === 0) return expenseRows;

    return [
        ...expenseRows,
        {
            id: 'journal-credit-vat',
            section: isCreditNote ? 'Avere' : vatSection,
            account: vatAccount,
            amount: totalVat,
            categories: ['IVA'],
            sourceLineIds: [],
            editable: false,
            note: protocol,
        },
    ];
};

const ORIGIN_LABEL = {
    sdi: 'SDI',
    cartacea: 'cartacea',
    manuale: 'manuale',
};

const getOriginLabel = (origin) => ORIGIN_LABEL[origin] || origin || 'canale sconosciuto';

const detectDuplicateInvoices = (invoices) => {
    const groupByKey = {};

    Object.values(invoices).forEach((invoice) => {
        if (!invoice) return;
        const essentials = extractInvoiceEssentials(invoice);
        const vat = (essentials.xmlSupplierVat || '').replace(/\s/g, '');
        const ref = (essentials.supplierReferenceNumber || '').trim().toLowerCase();
        if (!vat || !ref) return;
        const key = `${vat}#${ref}`;
        if (!groupByKey[key]) groupByKey[key] = [];
        groupByKey[key].push(invoice.id);
    });

    const result = {};
    Object.values(groupByKey).forEach((ids) => {
        if (ids.length < 2) return;
        ids.forEach((id) => {
            result[id] = ids.filter((other) => other !== id);
        });
    });

    return result;
};

const buildDuplicateMessage = (invoice, others) => {
    const essentials = extractInvoiceEssentials(invoice);
    const crossCompany = others.filter((o) => o.companyDocumentId !== invoice.companyDocumentId);
    const ref = essentials.supplierReferenceNumber || '';
    const supplierName = essentials.xmlSupplierName || 'fornitore';

    if (crossCompany.length > 0) {
        const companyIds = crossCompany.map((o) => o.companyDocumentId).join(', ');
        return `Possibile duplicato SDI satellite: la fattura ${ref} di ${supplierName} e presente anche per ${companyIds}. Verificare se e un transito intercompany o un inserimento duplicato.`;
    }

    const allOriginLabels = [getOriginLabel(invoice.origin), ...others.map((o) => getOriginLabel(o.origin))];
    const uniqueOrigins = [...new Set(allOriginLabels)].join(' e ');
    return `Possibile duplicato: la fattura ${ref} di ${supplierName} risulta presente come ${uniqueOrigins}. Verificare prima di contabilizzare.`;
};

const recalculateSingleInvoice = (workspace, invoiceId) => {
    const invoice = workspace.invoices[invoiceId];

    if (!invoice) {
        return null;
    }

    const normalized = extractInvoiceEssentials(invoice);
    const groupMap = buildGroupMap(workspace.merchandiseGroups);
    const articleMap = buildArticleMap(workspace.articleCatalog);
    const selectedDdts = (workspace.ddts || []).filter((ddt) => (invoice.selectedDdtIds || []).includes(ddt.id));
    const receivedInvoiceDefaults = workspace.accountingDefaults?.receivedInvoices || {};
    const fallbackAccount = receivedInvoiceDefaults.purchaseAccount || invoice.fallbackPurchaseAccount || null;

    const ddtLines = aggregateSelectedDdtLines(selectedDdts, invoice).map((line) =>
        buildComputedLine({
            line,
            sourceType: 'ddt',
            sourceLabel: line.sourceLabel,
            overrides: invoice.lineOverrides?.[line.id],
            groupMap,
            articleMap,
            fallbackAccount,
        })
    );

    const directLines = (invoice.directLines || []).map((line) =>
        buildComputedLine({
            line,
            sourceType: 'direct',
            sourceLabel: 'Documento diretto',
            overrides: invoice.lineOverrides?.[line.id],
            groupMap,
            articleMap,
            fallbackAccount,
        })
    );

    const extraLines = (invoice.extraLines || []).map((line) =>
        buildComputedLine({
            line,
            sourceType: 'extra',
            sourceLabel: 'Riga manuale',
            overrides: invoice.lineOverrides?.[line.id],
            groupMap,
            articleMap,
            fallbackAccount,
        })
    );

    const invoiceNet = roundMoney(normalized.xmlTotals?.net || 0);
    const invoiceVat = roundMoney(normalized.xmlTotals?.vat || 0);
    const declaredGross = normalized.xmlTotals?.declaredGross;
    const invoiceGross = Number.isFinite(declaredGross)
        ? roundMoney(declaredGross)
        : roundMoney(invoiceNet + invoiceVat);
    const computedLines = [...ddtLines, ...directLines, ...extraLines];
    const net = roundMoney(computedLines.reduce((total, line) => total + line.net, 0));
    const calculatedVat = roundMoney(computedLines.reduce((total, line) => total + line.vat, 0));
    const gross = roundMoney(net + calculatedVat);
    const matchedDdtNet = roundMoney(ddtLines.reduce((total, line) => total + line.matchedNet, 0));
    const matchedDdtVat = roundMoney(ddtLines.reduce((total, line) => total + line.matchedVat, 0));
    const difference = roundMoney(invoiceNet - net);
    const vatDifference = roundMoney(invoiceVat - calculatedVat);
    // Quadratura esatta al centesimo: nessuna tolleranza, nessun arrotondamento
    // a valle che possa assorbire lo scarto.
    const netMatches = difference === 0;
    const vatMatches = vatDifference === 0;
    const blockingIssues = [];

    if (!invoice.serviceFlow && invoice.documentType !== 'TD04' && selectedDdts.length === 0) {
        blockingIssues.push({
            id: 'ddt-required',
            level: 'warning',
            message: 'Seleziona almeno un DDT prima della contabilizzazione.',
        });
    }

    if (!netMatches) {
        blockingIssues.push({
            id: 'net-mismatch',
            level: 'error',
            message: `L'imponibile allocato non coincide con il documento: mancano ${difference.toFixed(2)} €.`,
        });
    }

    if (!vatMatches) {
        blockingIssues.push({
            id: 'vat-mismatch',
            level: 'error',
            message: `L'IVA allocata non coincide con il documento: mancano ${vatDifference.toFixed(2)} EUR.`,
        });
    }

    if (!invoice.supplierKnown) {
        blockingIssues.push({
            id: 'supplier-missing',
            level: 'info',
            message: 'Fornitore non censito: collega o crea l anagrafica prima di contabilizzare.',
        });
    }

    if (!fallbackAccount) {
        blockingIssues.push({
            id: 'purchase-default-missing',
            level: 'error',
            message: 'Configura il conto predefinito Acquisti merci nel Piano dei Conti.',
        });
    }

    if (!receivedInvoiceDefaults.vatAccount) {
        blockingIssues.push({
            id: 'vat-default-missing',
            level: 'error',
            message: 'Configura il conto predefinito IVA a credito nel Piano dei Conti.',
        });
    }

    const protocolPreview =
        invoice.protocolAssigned ||
        `Registro ${invoice.protocolRegister || 1} / Protocollo ${invoice.protocolNumber || 'simulato'}`;

    return {
        ...invoice,
        ...normalized,
        xmlPreview: invoice.xmlPreview || buildLegacyXmlPreview(normalized),
        pdfPreview: invoice.pdfPreview || [],
        computed: {
            selectedDdts,
            lines: computedLines,
            acubeSummary: buildAcubeSummary(invoice, normalized),
            totals: {
                net,
                vat: calculatedVat,
                gross,
                xmlNet: invoiceNet,
                xmlVat: invoiceVat,
                xmlGross: invoiceGross,
                difference,
                netMatches,
                vatDifference,
                vatMatches,
                matchedDdtNet,
                matchedDdtVat,
                matchedNetDifference: roundMoney(invoiceNet - matchedDdtNet),
                matchedVatDifference: roundMoney(invoiceVat - matchedDdtVat),
            },
            protocolPreview,
            journalDraft: buildJournalDraft(
                computedLines,
                protocolPreview,
                calculatedVat,
                receivedInvoiceDefaults.vatAccount,
                receivedInvoiceDefaults.vatSection || 'Dare',
                invoice.documentType === 'TD04'
            ),
            blockingIssues,
        },
    };
};

export const recalculatePassiveWorkspace = (workspace) => {
    const nextWorkspace = {
        ...workspace,
        invoices: { ...(workspace.invoices || {}) },
        ddts: [...(workspace.ddts || [])],
        supplierDirectory: [...(workspace.supplierDirectory || [])],
        merchandiseGroups: [...(workspace.merchandiseGroups || [])],
        articleCatalog: [...(workspace.articleCatalog || [])],
        salesEntries: [...(workspace.salesEntries || [])],
    };

    Object.keys(nextWorkspace.invoices).forEach((invoiceId) => {
        nextWorkspace.invoices[invoiceId] = recalculateSingleInvoice(nextWorkspace, invoiceId);
    });

    const suspectedDuplicates = detectDuplicateInvoices(nextWorkspace.invoices);
    Object.entries(suspectedDuplicates).forEach(([invoiceId, otherIds]) => {
        const invoice = nextWorkspace.invoices[invoiceId];
        if (!invoice) return;
        const alreadyTagged = invoice.computed.blockingIssues.some((issue) => issue.id === 'suspected-duplicate');
        if (alreadyTagged) return;
        const others = otherIds.map((id) => nextWorkspace.invoices[id]).filter(Boolean);
        invoice.computed.blockingIssues.push({
            id: 'suspected-duplicate',
            level: 'warning',
            message: buildDuplicateMessage(invoice, others),
        });
    });

    return nextWorkspace;
};
